import React, { useState } from "react";
import { Users, Store, Lock, Clock, Pencil, Check, X, Tag, ShoppingBag, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { IdeaCover } from "@/components/ideaos/shared";
import { bandColor, useBandLabel } from "@/components/ScoreRadial";
import DeleteProjectButton from "@/components/DeleteProjectButton";
import { useLang } from "@/i18n/LanguageContext";
import { CoverEditor } from "@/components/CoverEditor";
import { IdeaHistoryButton } from "@/components/IdeaHistory";
import { PublishButton } from "@/components/PublishDialog";

export const VAULT_FILTERS = [
  ["all", () => true],
  ["favorites", (p) => !!p.favorite],
  ["captured", (p) => ["captured"].includes(p.maturity || "captured")],
  ["exploring", (p) => ["defined", "explored"].includes(p.maturity)],
  ["testing", (p) => ["tested", "proven"].includes(p.maturity)],
  ["building", (p) => ["building", "launched"].includes(p.maturity)],
  ["scored", (p) => p.score?.total != null],
  ["shared", (p) => (p.collaborators || []).length > 0],
  ["market", (p) => !!p.listing_id || p.visibility === "marketplace"],
  ["parked", (p) => ["parked", "archived"].includes(p.maturity)],
];

function timeAgo(iso, lang) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return lang === "IT" ? "adesso" : "just now";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return lang === "IT" ? `${d}g fa` : `${d}d ago`;
}

/** Inline title rename on the card: PATCH /projects/{id}/name. */
function CardTitle({ p, compact, onRenamed }) {
  const { lang } = useLang();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.name);
  const [busy, setBusy] = useState(false);
  const commit = async () => {
    const n = name.trim();
    if (!n || n === p.name) { setEditing(false); setName(p.name); return; }
    setBusy(true);
    try { await api.patch(`/projects/${p.id}/name`, { name: n }); onRenamed?.(n); toast.success(lang === "IT" ? "Idea rinominata" : "Idea renamed"); setEditing(false); }
    catch { toast.error(lang === "IT" ? "Non riuscito" : "Failed"); }
    finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()} data-testid={`rename-form-${p.id}`}>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={120} disabled={busy} data-testid={`rename-input-${p.id}`}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditing(false); setName(p.name); } }}
          className="h-9 min-w-0 flex-1 rounded-full bg-white/95 px-3 text-sm font-semibold text-[#0b0f1a] outline-none ring-2 ring-[#7c3aed]" />
        <button type="button" onClick={commit} disabled={busy} aria-label="save" data-testid={`rename-save-${p.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#7c3aed] text-white"><Check size={13} /></button>
        <button type="button" onClick={() => { setEditing(false); setName(p.name); }} aria-label="cancel" data-testid={`rename-cancel-${p.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-slate-600"><X size={13} /></button>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-1.5">
      <h3 className={`min-w-0 flex-1 font-bold tracking-tight text-white leading-tight ${compact ? "text-lg line-clamp-2" : "text-xl sm:text-2xl line-clamp-3"}`} data-testid={`card-title-${p.id}`}>{p.name}</h3>
      <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }} aria-label={lang === "IT" ? "Rinomina" : "Rename"} title={lang === "IT" ? "Rinomina" : "Rename"} data-testid={`rename-btn-${p.id}`}
        className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-white/80 backdrop-blur transition-colors hover:bg-white hover:text-[#0b0f1a]"><Pencil size={12} /></button>
    </div>
  );
}

/** Star toggle: PATCH /projects/{id}/favorite (per user). */
function FavoriteButton({ p, onToggle }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try { const { data } = await api.patch(`/projects/${p.id}/favorite`); onToggle?.(data.favorite); }
    catch { toast.error("—"); }
    finally { setBusy(false); }
  };
  const label = p.favorite ? t("vault.fav_remove") : t("vault.fav_add");
  return (
    <button type="button" onClick={toggle} aria-pressed={!!p.favorite} aria-label={label} title={label} data-testid={`favorite-btn-${p.id}`}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-full backdrop-blur transition-[transform,background-color] active:scale-90 ${p.favorite ? "bg-[#f59e0b] text-white" : "bg-white/85 text-slate-500 hover:text-[#f59e0b]"}`}>
      <Star size={13} fill={p.favorite ? "currentColor" : "none"} />
    </button>
  );
}

/** Full-bleed 4:5 cover card: the image fills the whole card, badges/title/actions float on top. */
export function VaultCard({ p, pending, onOpen, onMaturity, onDeleted, onCover, onRenamed, onVisibility, onFavorite, compact = false }) {
  const { t, lang } = useLang();
  const bandLabel = useBandLabel();
  const score = p.score?.total, band = p.score?.band;
  return (
    <div data-testid={`project-card-${p.id}`} data-favorite={p.favorite ? "true" : "false"} className="group relative h-full overflow-hidden rounded-[1.75rem] bg-white ring-1 ring-slate-200 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[12px_12px_0_0_#ede9fe]">
      <IdeaCover id={p.id} url={p.cover_url} name={p.name} className="aspect-[4/5]">
        <button type="button" onClick={onOpen} className="absolute inset-0 block w-full" data-testid={`project-open-${p.id}`} aria-label={p.name} />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0b0f1a]/90 via-[#0b0f1a]/20 to-transparent" />
        <div className="absolute left-3 top-3 flex flex-wrap items-center gap-1.5">
          <FavoriteButton p={p} onToggle={onFavorite} />
          {["community", "public"].includes(p.visibility) && <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-[#0b0f1a] backdrop-blur" data-testid={`reactions-badge-${p.id}`}><Star size={10} className="text-[#f59e0b]" /> {p.reactions_count || 0}</span>}
          {p.collaborators?.length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-[#0b0f1a] backdrop-blur" data-testid={`shared-badge-${p.id}`}><Users size={10} /> {p.collaborators.length}</span>}
          {(p.listing_id || p.visibility === "marketplace") && <span className="inline-flex items-center gap-1 rounded-full bg-[#0b0f1a]/90 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid={`market-badge-${p.id}`}><Store size={10} /> Market</span>}
          {p.sold && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid={`sold-badge-${p.id}`}><Tag size={10} /> {lang === "IT" ? "Venduta" : "Sold"}</span>}
          {p.purchased && <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/90 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid={`purchased-badge-${p.id}`}><ShoppingBag size={10} /> {lang === "IT" ? "Acquistata" : "Purchased"}</span>}
          {pending && <span data-testid={`card-pending-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-semibold text-[#0b0f1a]"><Lock size={10} /> {t("dash.card_pending")}</span>}
        </div>
        {score != null ? (
          <span className="absolute right-3 top-3 rounded-2xl bg-white/95 px-2.5 py-1.5 text-center backdrop-blur" data-testid={`score-pill-${p.id}`}>
            <span className="block text-xl font-bold leading-none tabular-nums" style={{ color: bandColor(band) }}>{score}</span>
            <span className="block text-[8px] font-bold tracking-wider" style={{ color: bandColor(band) }}>{bandLabel(band)}</span>
          </span>
        ) : <span className="absolute right-3 top-3 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{t("vault.no_score")}</span>}
        <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
          <CardTitle p={p} compact={compact} onRenamed={onRenamed} />
          {p.next_move?.title && !compact && <p className="mt-1.5 line-clamp-2 text-xs text-slate-300"><span className="text-[#c4b5fd]">{t("dash.next")}:</span> {p.next_move.title}</p>}
          <div className="mt-3 flex items-center justify-between gap-2" data-testid={`card-actions-${p.id}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <IdeaHistoryButton projectId={p.id} />
              {p.is_owner !== false && <PublishButton p={p} onVisibility={onVisibility} onCover={onCover} onRenamed={onRenamed} />}
              {!compact && <span className="inline-flex items-center gap-1 text-[11px] text-white/60"><Clock size={11} /> {timeAgo(p.updated_at, lang)}</span>}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <CoverEditor projectId={p.id} coverUrl={p.cover_url} coverAuto={!!p.cover_auto} compact onChanged={onCover} />
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 ring-1 ring-slate-200 backdrop-blur"><DeleteProjectButton projectId={p.id} onDeleted={onDeleted} /></span>
            </div>
          </div>
        </div>
      </IdeaCover>
    </div>
  );
}
